/**
 * Stockage du token d'authentification — **en mémoire uniquement** (v1.02+).
 *
 * Le JWT n'est plus jamais écrit dans `localStorage` ni `sessionStorage` —
 * il vit exclusivement :
 *   1. Dans un **cookie httpOnly + Secure + SameSite=Lax** posé par le
 *      backend sur `/auth/login` (inaccessible depuis JavaScript → mitige XSS).
 *   2. En **variable JS locale au module** (in-memory) pour rester
 *      disponible aux composants qui lisent encore `Bearer ${token}` dans
 *      leurs headers axios (rétro-compatibilité pendant la transition).
 *
 * Conséquences :
 *   - Reload de la page → la variable in-memory est vidée. Le composant racine
 *     (`App.js`) doit appeler `/auth/me` en s'appuyant sur le cookie pour
 *     restaurer la session AVANT de décider de rediriger vers /auth.
 *   - Multi-onglets → chaque onglet repart d'une variable vide mais récupère
 *     l'utilisateur via le cookie (partagé entre onglets du même domaine).
 *   - Fermeture du navigateur → le cookie persiste (max-age=7j) mais
 *     `enforceUnloadGap()` (lib/sessionTimeout.js) force un logout si l'écart
 *     est > 5 min, comportement bancaire attendu pour Auto-Gère.
 *
 * Migration douce :
 *   - Au premier accès, on purge l'éventuel ancien token présent dans
 *     localStorage/sessionStorage (utilisateurs des versions précédentes).
 */

const STORAGE_KEY = 'token';

let _inMemoryToken = null;
let _migrated = false;

const purgeLegacyStorage = () => {
  if (_migrated) return;
  _migrated = true;
  try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  try { window.sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
};

export const getToken = () => {
  purgeLegacyStorage();
  return _inMemoryToken;
};

export const setToken = (token) => {
  purgeLegacyStorage();
  _inMemoryToken = token || null;
};

export const clearToken = () => setToken(null);
